import { useState, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useGetTenantBySlugQuery } from '@/api/tenants/getTenantBySlug';
import { apiClient } from '@/api/api-client.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/react-query.config';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/dashboard/tenant/$tenantCode/branding')({
  component: TenantBrandingRoute,
});

function TenantBrandingRoute() {
  const { tenantCode } = Route.useParams();
  const { data: tenant, isLoading } = useGetTenantBySlugQuery(tenantCode);
  const queryClient = useQueryClient();

  const [nameEn, setNameEn] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');

  useEffect(() => {
    if (tenant) {
      setNameEn(tenant.nameEn || tenant.title || '');
      setDescriptionEn((tenant as any).descriptionEn || '');
    }
  }, [tenant]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, string>) =>
      apiClient.put('/api/admin/tenants/{id}' as any, data, {
        pathParams: { id: tenant?.id },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenants.detail(tenantCode) });
      toast.success('Tenant updated successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update tenant');
    },
  });

  const handleSave = () => {
    const name = nameEn.trim();
    updateMutation.mutate({
      title: name,
      name_en: name,
      description_en: descriptionEn.trim(),
    });
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Code:</span>
        <Badge variant="outline">{tenantCode.toUpperCase()}</Badge>
      </div>

      <div className="grid gap-4 max-w-lg">
        <div className="space-y-2">
          <Label htmlFor="name-en">Name</Label>
          <Input
            id="name-en"
            value={nameEn}
            onChange={e => setNameEn(e.target.value)}
            placeholder="Tenant name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description-en">Description</Label>
          <Textarea
            id="description-en"
            value={descriptionEn}
            onChange={e => setDescriptionEn(e.target.value)}
            placeholder="Brief description of this tenant..."
            rows={3}
          />
        </div>

        <Button
          onClick={handleSave}
          disabled={updateMutation.isPending || !nameEn.trim()}
          className="w-fit"
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
